import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Badge, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faBullseye,
  faLayerGroup,
  faCheck,
  faLink,
  faTrash,
  faPen,
  faCircleCheck,
  faTriangleExclamation,
  faCircleXmark,
  faHashtag,
  faPercent,
  faCoins,
  faSquareCheck,
  faSitemap,
  faListCheck,
  faGaugeHigh,
  faCalendarDays,
  faStar,
  faCalendarDay,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import { useWorkspace } from "../context/WorkspaceContext";
import {
  fetchCiclos, criarCiclo, analisarCicloComIA,
  fetchAreas, criarArea,
  fetchObjetivos, criarObjetivo, atualizarObjetivo, removerObjetivo,
  fetchResultadosChave, criarResultadoChave, atualizarResultadoChave, definirEstrategiaAgregacao,
  definirNota,
  removerResultadoChave,
  registrarCheckin,
  fetchIniciativas, criarIniciativa, vincularIniciativa, removerIniciativa,
  ESTRATEGIAS_AGREGACAO, TIPOS_METRICA,
} from "../api/okrApi";
import "./OkrPage.css";

// Visual completo por confianca: icone + cor + glow, usado pra colorir de
// verdade o board (borda, badge, barra de progresso) - nao so um texto.
const CONFIANCA_UI = {
  on_track: { label: "No caminho", color: "#10b981", glow: "rgba(16,185,129,0.4)", icon: faCircleCheck },
  at_risk: { label: "Em risco", color: "#f59e0b", glow: "rgba(245,158,11,0.4)", icon: faTriangleExclamation },
  behind: { label: "Atrasado", color: "#ef4444", glow: "rgba(239,68,68,0.4)", icon: faCircleXmark },
};
const confiancaUi = (codigo) => CONFIANCA_UI[codigo] || CONFIANCA_UI.on_track;

const TIPO_METRICA_ICON = {
  numero: faHashtag,
  percentual: faPercent,
  moeda: faCoins,
  booleano: faSquareCheck,
};

// Nota de fechamento (0-100) e so um numero no banco - o rotulo ("Superou as
// expectativas" etc.) e so derivado da faixa aqui, nunca guardado como texto.
const notaUi = (nota) => {
  if (nota === null || nota === undefined) {
    return { label: "Nao avaliado", color: "#94a3b8", glow: "rgba(148,163,184,0.25)" };
  }
  if (nota >= 100) return { label: "Superou as expectativas", color: "#84cc16", glow: "rgba(132,204,22,0.4)" };
  if (nota >= 75) return { label: "Atendeu a maioria das expectativas", color: "#ec4899", glow: "rgba(236,72,153,0.4)" };
  if (nota >= 50) return { label: "Atendeu algumas expectativas", color: "#06b6d4", glow: "rgba(6,182,212,0.4)" };
  if (nota >= 25) return { label: "Atendeu poucas expectativas", color: "#f59e0b", glow: "rgba(245,158,11,0.4)" };
  return { label: "Nao atingiu o esperado", color: "#ef4444", glow: "rgba(239,68,68,0.4)" };
};

// Area e cadastravel pelo usuario (sem cor propria no banco) - a cor vem de
// uma paleta fixa, escolhida deterministicamente pelo nome (mesma area =
// sempre a mesma cor, sem precisar guardar isso em lugar nenhum).
const AREA_PALETTE = [
  { color: "#6366f1", glow: "rgba(99,102,241,0.35)" },
  { color: "#ec4899", glow: "rgba(236,72,153,0.35)" },
  { color: "#10b981", glow: "rgba(16,185,129,0.35)" },
  { color: "#f59e0b", glow: "rgba(245,158,11,0.35)" },
  { color: "#3b82f6", glow: "rgba(59,130,246,0.35)" },
  { color: "#8b5cf6", glow: "rgba(139,92,246,0.35)" },
  { color: "#06b6d4", glow: "rgba(6,182,212,0.35)" },
  { color: "#f97316", glow: "rgba(249,115,22,0.35)" },
];
const corParaArea = (nome) => {
  let hash = 0;
  for (let i = 0; i < (nome || "").length; i += 1) hash = (hash * 31 + nome.charCodeAt(i)) >>> 0;
  return AREA_PALETTE[hash % AREA_PALETTE.length];
};

const iniciaisDoNome = (nome) => {
  if (!nome) return "?";
  const partes = String(nome).trim().split(/\s+/);
  const primeira = partes[0]?.[0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase() || "?";
};

const progressoPercentual = (kr) => {
  const inicial = Number(kr.valor_inicial) || 0;
  const atual = Number(kr.valor_atual) || 0;
  const alvo = Number(kr.valor_alvo) || 0;
  if (alvo === inicial) return atual >= alvo ? 100 : 0;
  const pct = ((atual - inicial) / (alvo - inicial)) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

const progressoMedio = (krs) => {
  if (!krs.length) return 0;
  return Math.round(krs.reduce((soma, kr) => soma + progressoPercentual(kr), 0) / krs.length);
};

const OkrPage = () => {
  const { selectedWorkspaceId } = useWorkspace();
  const workspaceId = selectedWorkspaceId ? Number(selectedWorkspaceId) : null;

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [ciclos, setCiclos] = useState([]);
  const [idCicloSelecionado, setIdCicloSelecionado] = useState(null);
  const [areas, setAreas] = useState([]);
  const [objetivos, setObjetivos] = useState([]);
  const [resultadosPorObjetivo, setResultadosPorObjetivo] = useState({});
  const [iniciativasPorKr, setIniciativasPorKr] = useState({});
  const [membros, setMembros] = useState([]);

  const [showNovoCiclo, setShowNovoCiclo] = useState(false);
  const [showNovoObjetivo, setShowNovoObjetivo] = useState(false);
  const [showEditarObjetivo, setShowEditarObjetivo] = useState(null); // objetivo ou null
  const [showNovoKr, setShowNovoKr] = useState(null); // idObjetivo ou null
  const [showEditarKr, setShowEditarKr] = useState(null); // kr ou null
  const [showAvaliarKr, setShowAvaliarKr] = useState(null); // kr ou null
  const [showCheckin, setShowCheckin] = useState(null); // kr ou null
  const [showIniciativa, setShowIniciativa] = useState(null); // kr ou null
  const [showNovaArea, setShowNovaArea] = useState(false);
  const [showAnaliseIA, setShowAnaliseIA] = useState(false);
  const [analisandoIA, setAnalisandoIA] = useState(false);
  const [descricaoIniciativaSugerida, setDescricaoIniciativaSugerida] = useState("");
  const [visaoAtual, setVisaoAtual] = useState("objetivo"); // "objetivo" | "area"

  // ── Carregamento ────────────────────────────────────────────────────────

  // carregarBase nao e memoizada - so precisa rodar de novo quando o
  // workspace muda, nunca por causa da propria funcao ser recriada a cada render.
  useEffect(() => {
    if (!workspaceId) {
      setCiclos([]);
      setCarregando(false);
      return;
    }
    carregarBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function carregarBase() {
    setCarregando(true);
    setErro("");
    try {
      const [ciclosRes, areasRes, membrosRes] = await Promise.all([
        fetchCiclos(workspaceId),
        fetchAreas(workspaceId),
        api.get(`/workspaces/${workspaceId}/members`),
      ]);
      const listaCiclos = ciclosRes.data?.data || [];
      setCiclos(listaCiclos);
      setAreas(areasRes.data?.data || []);
      setMembros(membrosRes.data?.records || membrosRes.data || []);

      if (listaCiclos.length && !idCicloSelecionado) {
        setIdCicloSelecionado(listaCiclos[0].id);
      }
    } catch (error) {
      console.error("OkrPage: erro ao carregar", error);
      setErro(error?.response?.data?.message || "Nao foi possivel carregar os OKRs.");
    } finally {
      setCarregando(false);
    }
  }

  // Mesmo motivo do efeito acima: so depende do ciclo selecionado.
  useEffect(() => {
    if (!idCicloSelecionado) {
      setObjetivos([]);
      return;
    }
    carregarObjetivos(idCicloSelecionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCicloSelecionado]);

  async function carregarObjetivos(idCiclo) {
    try {
      const res = await fetchObjetivos(idCiclo);
      const lista = res.data?.data || [];
      setObjetivos(lista);
      await Promise.all(lista.map((obj) => carregarResultadosChave(obj.id)));
    } catch (error) {
      console.error("OkrPage: erro ao carregar objetivos", error);
      setErro(error?.response?.data?.message || "Nao foi possivel carregar os objetivos.");
    }
  }

  async function carregarResultadosChave(idObjetivo) {
    try {
      const res = await fetchResultadosChave(idObjetivo);
      const lista = res.data?.data || [];
      setResultadosPorObjetivo((atual) => ({ ...atual, [idObjetivo]: lista }));
      await Promise.all(lista.map((kr) => carregarIniciativas(kr.id)));
    } catch (error) {
      console.error("OkrPage: erro ao carregar Key Results", error);
    }
  }

  async function carregarIniciativas(idResultadoChave) {
    try {
      const res = await fetchIniciativas(idResultadoChave);
      setIniciativasPorKr((atual) => ({ ...atual, [idResultadoChave]: res.data?.data || [] }));
    } catch (error) {
      console.error("OkrPage: erro ao carregar iniciativas", error);
    }
  }

  const objetivosAgrupados = useMemo(() => {
    // Objetivos-raiz primeiro (sem pai); filhos aparecem logo abaixo do pai.
    const raiz = objetivos.filter((o) => !o.id_objetivo_pai);
    const filhosPorPai = {};
    objetivos.forEach((o) => {
      if (o.id_objetivo_pai) {
        filhosPorPai[o.id_objetivo_pai] = filhosPorPai[o.id_objetivo_pai] || [];
        filhosPorPai[o.id_objetivo_pai].push(o);
      }
    });
    return { raiz, filhosPorPai };
  }, [objetivos]);

  const todosKrs = useMemo(() => Object.values(resultadosPorObjetivo).flat(), [resultadosPorObjetivo]);

  // Visao "Por Area": agrupa TODOS os KRs por area, atravessando objetivos -
  // diferente do agrupamento dentro de ObjetivoCard, que so olha os KRs de
  // UM objetivo por vez. Cada KR carrega o titulo do seu objetivo, pra dar
  // contexto sem precisar ir ate o outro modo de visao.
  const areasAgrupadas = useMemo(() => {
    const grupos = {};
    objetivos.forEach((obj) => {
      (resultadosPorObjetivo[obj.id] || []).forEach((kr) => {
        const nomeArea = kr.area_nome || "Geral";
        grupos[nomeArea] = grupos[nomeArea] || [];
        grupos[nomeArea].push({ ...kr, objetivo_titulo: obj.titulo });
      });
    });
    return grupos;
  }, [objetivos, resultadosPorObjetivo]);
  const statsConfianca = useMemo(() => {
    const contagem = { on_track: 0, at_risk: 0, behind: 0 };
    todosKrs.forEach((kr) => {
      contagem[kr.confianca] = (contagem[kr.confianca] || 0) + 1;
    });
    return contagem;
  }, [todosKrs]);

  const cicloSelecionado = useMemo(
    () => ciclos.find((c) => c.id === idCicloSelecionado) || null,
    [ciclos, idCicloSelecionado]
  );

  // ── Acoes ───────────────────────────────────────────────────────────────

  async function handleAnalisarIA() {
    setAnalisandoIA(true);
    setErro("");
    try {
      const res = await analisarCicloComIA(idCicloSelecionado);
      const atualizado = res.data?.data;
      setCiclos((atual) => atual.map((c) => (c.id === atualizado.id ? atualizado : c)));
    } catch (error) {
      console.error("OkrPage: erro ao analisar ciclo com IA", error);
      setErro(error?.response?.data?.message || "Nao foi possivel gerar a analise de IA.");
    } finally {
      setAnalisandoIA(false);
    }
  }

  function handleCriarTarefaDaAnalise(idKr, tituloSugerido) {
    const kr = todosKrs.find((item) => item.id === idKr);
    if (!kr) {
      setErro("Nao foi possivel localizar o Key Result sugerido pela IA (pode ter sido removido).");
      return;
    }
    setDescricaoIniciativaSugerida(tituloSugerido || "");
    setShowAnaliseIA(false);
    setShowIniciativa(kr);
  }

  async function handleNovoCiclo(dados) {
    const res = await criarCiclo({ idWorkspace: workspaceId, ...dados });
    const novo = res.data?.data;
    setCiclos((atual) => [novo, ...atual]);
    setIdCicloSelecionado(novo.id);
    setShowNovoCiclo(false);
  }

  async function handleNovaArea(nome) {
    const res = await criarArea({ idWorkspace: workspaceId, nome });
    setAreas((atual) => [...atual, res.data?.data]);
    setShowNovaArea(false);
  }

  async function handleNovoObjetivo(dados) {
    const res = await criarObjetivo({ idCiclo: idCicloSelecionado, ...dados });
    const novo = res.data?.data;
    setObjetivos((atual) => [...atual, novo]);
    setResultadosPorObjetivo((atual) => ({ ...atual, [novo.id]: [] }));
    setShowNovoObjetivo(false);
  }

  async function handleEditarObjetivo(objetivo, dados) {
    const res = await atualizarObjetivo(objetivo.id, dados);
    const atualizado = res.data?.data;
    setObjetivos((atual) => atual.map((item) => (item.id === atualizado.id ? atualizado : item)));
    setShowEditarObjetivo(null);
  }

  async function handleRemoverObjetivo(objetivo) {
    await removerObjetivo(objetivo.id);
    setObjetivos((atual) => atual.filter((item) => item.id !== objetivo.id));
    setResultadosPorObjetivo((atual) => {
      const proximo = { ...atual };
      delete proximo[objetivo.id];
      return proximo;
    });
  }

  async function handleNovoKr(idObjetivo, dados) {
    // estrategiaAgregacao aqui configura o KR-PAI escolhido (idResultadoChavePai),
    // nao o KR que esta nascendo agora - por isso sai do payload de criacao e
    // vira uma chamada separada, depois que o filho ja existe.
    const { idResultadoChavePai, estrategiaAgregacao, ...dadosDoNovoKr } = dados;

    const res = await criarResultadoChave({ idObjetivo, idResultadoChavePai, ...dadosDoNovoKr });
    const novo = res.data?.data;
    setResultadosPorObjetivo((atual) => ({
      ...atual,
      [idObjetivo]: [...(atual[idObjetivo] || []), novo],
    }));
    setIniciativasPorKr((atual) => ({ ...atual, [novo.id]: [] }));

    if (idResultadoChavePai && estrategiaAgregacao) {
      const paiRes = await definirEstrategiaAgregacao(idResultadoChavePai, estrategiaAgregacao);
      const paiAtualizado = paiRes.data?.data;
      setResultadosPorObjetivo((atual) => {
        const proximo = { ...atual };
        Object.keys(proximo).forEach((idObj) => {
          proximo[idObj] = proximo[idObj].map((item) =>
            item.id === paiAtualizado.id ? paiAtualizado : item
          );
        });
        return proximo;
      });
    }

    setShowNovoKr(null);
  }

  async function handleEditarKr(idObjetivo, kr, dados) {
    const res = await atualizarResultadoChave(kr.id, dados);
    const atualizado = res.data?.data;
    setResultadosPorObjetivo((atual) => ({
      ...atual,
      [idObjetivo]: (atual[idObjetivo] || []).map((item) => (item.id === atualizado.id ? atualizado : item)),
    }));
    setShowEditarKr(null);
  }

  async function handleAvaliarKr(idObjetivo, kr, notaFinal) {
    const res = await definirNota(kr.id, notaFinal);
    const atualizado = res.data?.data;
    setResultadosPorObjetivo((atual) => ({
      ...atual,
      [idObjetivo]: (atual[idObjetivo] || []).map((item) => (item.id === atualizado.id ? atualizado : item)),
    }));
    setShowAvaliarKr(null);
  }

  async function handleCheckin(kr, dados) {
    const res = await registrarCheckin({ idResultadoChave: kr.id, ...dados });
    const { resultadoChave, pai, objetivo } = res.data?.data || {};
    // Atualiza o KR que recebeu o check-in e, se existir, o pai recalculado
    // sozinho pelo rollup - sem recarregar a tela inteira.
    setResultadosPorObjetivo((atual) => {
      const proximo = { ...atual };
      Object.keys(proximo).forEach((idObj) => {
        proximo[idObj] = proximo[idObj].map((item) => {
          if (resultadoChave && item.id === resultadoChave.id) return resultadoChave;
          if (pai && item.id === pai.id) return pai;
          return item;
        });
      });
      return proximo;
    });
    if (objetivo) {
      setObjetivos((atual) => atual.map((item) => (item.id === objetivo.id ? objetivo : item)));
    }
    setShowCheckin(null);
  }

  async function handleRemoverKr(idObjetivo, kr) {
    const res = await removerResultadoChave(kr.id);
    const { pai, objetivo } = res.data?.data || {};
    setResultadosPorObjetivo((atual) => {
      const proximo = { ...atual };
      proximo[idObjetivo] = (proximo[idObjetivo] || []).filter((item) => item.id !== kr.id);
      if (pai) {
        Object.keys(proximo).forEach((idObj) => {
          proximo[idObj] = proximo[idObj].map((item) => (item.id === pai.id ? pai : item));
        });
      }
      return proximo;
    });
    if (objetivo) {
      setObjetivos((atual) => atual.map((item) => (item.id === objetivo.id ? objetivo : item)));
    }
  }

  async function handleCriarIniciativa(kr, dados) {
    await criarIniciativa(kr.id, dados);
    await carregarIniciativas(kr.id);
    setShowIniciativa(null);
    setDescricaoIniciativaSugerida("");
  }

  async function handleVincularIniciativa(kr, idTarefa) {
    await vincularIniciativa(kr.id, idTarefa);
    await carregarIniciativas(kr.id);
    setShowIniciativa(null);
  }

  async function handleRemoverIniciativa(kr, idIniciativa) {
    await removerIniciativa(idIniciativa);
    await carregarIniciativas(kr.id);
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (!workspaceId) {
    return <Alert variant="info" className="m-4">Selecione um workspace para ver os OKRs.</Alert>;
  }

  if (carregando) {
    return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="okr-page">
      <div className="okr-orb okr-orb-1" />
      <div className="okr-orb okr-orb-2" />

      <div className="okr-hero">
        <div className="okr-hero-icon">
          <FontAwesomeIcon icon={faBullseye} />
        </div>
        <div className="okr-hero-info">
          <p className="okr-hero-eyebrow">Gestao por objetivos</p>
          <h1 className="okr-hero-title">OKRs</h1>
          <div className="okr-ciclo-select">
            <FontAwesomeIcon icon={faCalendarDays} className="okr-ciclo-icon" />
            <Form.Select
              value={idCicloSelecionado || ""}
              onChange={(e) => setIdCicloSelecionado(Number(e.target.value))}
            >
              {ciclos.length === 0 && <option value="">Nenhum ciclo ainda</option>}
              {ciclos.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </Form.Select>
            <button type="button" className="okr-btn-ghost" onClick={() => setShowNovoCiclo(true)}>
              <FontAwesomeIcon icon={faPlus} /> Ciclo
            </button>
          </div>
        </div>
        <div className="okr-hero-actions">
          <button type="button" className="okr-btn-ghost" onClick={() => setShowNovaArea(true)}>
            <FontAwesomeIcon icon={faLayerGroup} /> Nova area
          </button>
          {idCicloSelecionado && (
            <button type="button" className="okr-btn-ghost" onClick={() => setShowAnaliseIA(true)}>
              <FontAwesomeIcon icon={faWandMagicSparkles} /> Analise de IA
            </button>
          )}
          {idCicloSelecionado && (
            <button type="button" className="okr-btn-cta" onClick={() => setShowNovoObjetivo(true)}>
              <FontAwesomeIcon icon={faPlus} /> Objetivo
            </button>
          )}
        </div>
      </div>

      {erro && <Alert variant="danger">{erro}</Alert>}

      {idCicloSelecionado && objetivosAgrupados.raiz.length > 0 && (
        <div className="okr-stats-row">
          <div className="okr-stat-card">
            <div className="okr-stat-icon" style={{ background: "rgba(99,102,241,0.16)", color: "#818cf8" }}>
              <FontAwesomeIcon icon={faBullseye} />
            </div>
            <div>
              <strong>{objetivos.length}</strong>
              <small>Objetivos</small>
            </div>
          </div>
          <div className="okr-stat-card">
            <div className="okr-stat-icon" style={{ background: "rgba(59,130,246,0.16)", color: "#60a5fa" }}>
              <FontAwesomeIcon icon={faListCheck} />
            </div>
            <div>
              <strong>{todosKrs.length}</strong>
              <small>Key Results</small>
            </div>
          </div>
          <div className="okr-stat-card">
            <div className="okr-stat-icon" style={{ background: "rgba(16,185,129,0.16)", color: "#34d399" }}>
              <FontAwesomeIcon icon={faGaugeHigh} />
            </div>
            <div>
              <strong>{progressoMedio(todosKrs)}%</strong>
              <small>Progresso medio</small>
            </div>
          </div>
          <div className="okr-stat-card okr-stat-confianca">
            <div className="okr-stat-confianca-item" style={{ color: CONFIANCA_UI.on_track.color }}>
              <FontAwesomeIcon icon={CONFIANCA_UI.on_track.icon} />
              <strong>{statsConfianca.on_track}</strong>
            </div>
            <div className="okr-stat-confianca-item" style={{ color: CONFIANCA_UI.at_risk.color }}>
              <FontAwesomeIcon icon={CONFIANCA_UI.at_risk.icon} />
              <strong>{statsConfianca.at_risk}</strong>
            </div>
            <div className="okr-stat-confianca-item" style={{ color: CONFIANCA_UI.behind.color }}>
              <FontAwesomeIcon icon={CONFIANCA_UI.behind.icon} />
              <strong>{statsConfianca.behind}</strong>
            </div>
            <small className="okr-stat-confianca-label">Confianca dos KRs</small>
          </div>
        </div>
      )}

      {idCicloSelecionado && objetivosAgrupados.raiz.length > 0 && (
        <div className="okr-view-toggle">
          <button
            type="button"
            className={visaoAtual === "objetivo" ? "ativo" : ""}
            onClick={() => setVisaoAtual("objetivo")}
          >
            <FontAwesomeIcon icon={faBullseye} /> Por Objetivo
          </button>
          <button
            type="button"
            className={visaoAtual === "area" ? "ativo" : ""}
            onClick={() => setVisaoAtual("area")}
          >
            <FontAwesomeIcon icon={faLayerGroup} /> Por Area
          </button>
        </div>
      )}

      {idCicloSelecionado && objetivosAgrupados.raiz.length === 0 && (
        <div className="okr-vazio">
          <FontAwesomeIcon icon={faBullseye} size="2x" />
          <p>Nenhum objetivo neste ciclo ainda. Comece criando um.</p>
        </div>
      )}

      {visaoAtual === "objetivo" ? (
        <div className="okr-board">
        {objetivosAgrupados.raiz.map((objetivo) => (
          <ObjetivoCard
            key={objetivo.id}
            objetivo={objetivo}
            filhos={objetivosAgrupados.filhosPorPai[objetivo.id] || []}
            resultadosPorObjetivo={resultadosPorObjetivo}
            iniciativasPorKr={iniciativasPorKr}
            onEditarObjetivo={(obj) => setShowEditarObjetivo(obj)}
            onRemoverObjetivo={handleRemoverObjetivo}
            onNovoKr={(idObj) => setShowNovoKr(idObj)}
            onEditarKr={(kr) => setShowEditarKr(kr)}
            onAvaliarKr={(kr) => setShowAvaliarKr(kr)}
            onCheckin={(kr) => setShowCheckin(kr)}
            onIniciativa={(kr) => setShowIniciativa(kr)}
            onRemoverIniciativa={handleRemoverIniciativa}
            onRemoverKr={handleRemoverKr}
          />
        ))}
        </div>
      ) : (
        <div className="okr-board">
        {Object.entries(areasAgrupadas).map(([nomeArea, krs]) => (
          <AreaCard
            key={nomeArea}
            nomeArea={nomeArea}
            krs={krs}
            iniciativasPorKr={iniciativasPorKr}
            onEditarKr={(kr) => setShowEditarKr(kr)}
            onAvaliarKr={(kr) => setShowAvaliarKr(kr)}
            onCheckin={(kr) => setShowCheckin(kr)}
            onIniciativa={(kr) => setShowIniciativa(kr)}
            onRemoverIniciativa={handleRemoverIniciativa}
            onRemoverKr={handleRemoverKr}
          />
        ))}
        </div>
      )}

      {showNovoCiclo && (
        <ModalNovoCiclo onClose={() => setShowNovoCiclo(false)} onSalvar={handleNovoCiclo} />
      )}
      {showNovaArea && (
        <ModalNovaArea onClose={() => setShowNovaArea(false)} onSalvar={handleNovaArea} />
      )}
      {showAnaliseIA && cicloSelecionado && (
        <ModalAnaliseIA
          ciclo={cicloSelecionado}
          analisando={analisandoIA}
          onClose={() => setShowAnaliseIA(false)}
          onAnalisar={handleAnalisarIA}
          onCriarTarefa={handleCriarTarefaDaAnalise}
        />
      )}
      {showNovoObjetivo && (
        <ModalNovoObjetivo
          objetivosExistentes={objetivos}
          onClose={() => setShowNovoObjetivo(false)}
          onSalvar={handleNovoObjetivo}
        />
      )}
      {showNovoKr && (
        <ModalNovoKr
          idObjetivo={showNovoKr}
          areas={areas}
          membros={membros}
          resultadosChaveExistentes={Object.values(resultadosPorObjetivo).flat()}
          onClose={() => setShowNovoKr(null)}
          onSalvar={(dados) => handleNovoKr(showNovoKr, dados)}
        />
      )}
      {showEditarObjetivo && (
        <ModalEditarObjetivo
          objetivo={showEditarObjetivo}
          objetivosExistentes={objetivos}
          onClose={() => setShowEditarObjetivo(null)}
          onSalvar={(dados) => handleEditarObjetivo(showEditarObjetivo, dados)}
        />
      )}
      {showEditarKr && (
        <ModalEditarKr
          kr={showEditarKr}
          areas={areas}
          membros={membros}
          onClose={() => setShowEditarKr(null)}
          onSalvar={(dados) => handleEditarKr(showEditarKr.id_objetivo, showEditarKr, dados)}
        />
      )}
      {showAvaliarKr && (
        <ModalAvaliarKr
          kr={showAvaliarKr}
          onClose={() => setShowAvaliarKr(null)}
          onSalvar={(notaFinal) => handleAvaliarKr(showAvaliarKr.id_objetivo, showAvaliarKr, notaFinal)}
        />
      )}
      {showCheckin && (
        <ModalCheckin kr={showCheckin} onClose={() => setShowCheckin(null)} onSalvar={(dados) => handleCheckin(showCheckin, dados)} />
      )}
      {showIniciativa && (
        <ModalIniciativa
          kr={showIniciativa}
          membros={membros}
          descricaoInicial={descricaoIniciativaSugerida}
          onClose={() => {
            setShowIniciativa(null);
            setDescricaoIniciativaSugerida("");
          }}
          onCriar={(dados) => handleCriarIniciativa(showIniciativa, dados)}
          onVincular={(idTarefa) => handleVincularIniciativa(showIniciativa, idTarefa)}
        />
      )}
    </div>
  );
};

// ── Subcomponentes ───────────────────────────────────────────────────────

const ObjetivoCard = ({
  objetivo, filhos, resultadosPorObjetivo, iniciativasPorKr, ehFilho,
  onEditarObjetivo, onRemoverObjetivo, onNovoKr, onEditarKr, onAvaliarKr, onCheckin, onIniciativa, onRemoverIniciativa, onRemoverKr,
}) => {
  const krsDoObjetivo = useMemo(() => resultadosPorObjetivo[objetivo.id] || [], [resultadosPorObjetivo, objetivo.id]);
  const porArea = useMemo(() => {
    const grupos = {};
    krsDoObjetivo.forEach((kr) => {
      const nome = kr.area_nome || "Geral";
      grupos[nome] = grupos[nome] || [];
      grupos[nome].push(kr);
    });
    return grupos;
  }, [krsDoObjetivo]);

  const ui = confiancaUi(objetivo.confianca);
  const pct = progressoMedio(krsDoObjetivo);

  return (
    <div className={`okr-objetivo-card${ehFilho ? " okr-objetivo-card-filho" : ""}`} style={{ "--conf-color": ui.color, "--conf-glow": ui.glow }}>
      <div className="okr-objetivo-head">
        <div className="okr-objetivo-icon" style={{ background: `${ui.color}22`, color: ui.color, boxShadow: `0 0 18px ${ui.glow}` }}>
          <FontAwesomeIcon icon={ehFilho ? faSitemap : faBullseye} />
        </div>
        <div className="okr-objetivo-heading">
          <h4>{objetivo.titulo}</h4>
          {objetivo.descricao && <p className="okr-objetivo-desc">{objetivo.descricao}</p>}
        </div>
        <div className="okr-objetivo-head-actions">
          <button type="button" className="okr-icon-btn" onClick={() => onEditarObjetivo(objetivo)} title="Editar objetivo">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button
            type="button"
            className="okr-icon-btn okr-icon-btn-danger"
            title="Excluir objetivo"
            onClick={() => {
              if (window.confirm(`Excluir o objetivo "${objetivo.titulo}"? Os Key Results dele tambem serao removidos. Essa acao nao pode ser desfeita.`)) {
                onRemoverObjetivo(objetivo);
              }
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <span className="okr-confianca-pill" style={{ color: ui.color, background: `${ui.color}1a`, borderColor: `${ui.color}44` }}>
            <FontAwesomeIcon icon={ui.icon} /> {ui.label}
          </span>
        </div>
      </div>

      <div className="okr-objetivo-progress">
        <div className="okr-objetivo-progress-bar">
          <div className="okr-objetivo-progress-fill" style={{ width: `${pct}%`, background: ui.color, boxShadow: `0 0 10px ${ui.glow}` }} />
        </div>
        <span className="okr-objetivo-progress-label">
          <strong>{pct}%</strong> concluido · {krsDoObjetivo.length} Key Result{krsDoObjetivo.length === 1 ? "" : "s"}
        </span>
      </div>

      {Object.entries(porArea).map(([nomeArea, lista]) => {
        const areaUi = corParaArea(nomeArea);
        return (
          <div key={nomeArea} className="okr-area-grupo">
            <div className="okr-area-titulo">
              <span className="okr-area-dot" style={{ background: areaUi.color, boxShadow: `0 0 8px ${areaUi.glow}` }} />
              {nomeArea}
            </div>
            {lista.map((kr) => (
              <KrRow
                key={kr.id}
                kr={kr}
                iniciativas={iniciativasPorKr[kr.id] || []}
                onEditar={() => onEditarKr(kr)}
                onAvaliar={() => onAvaliarKr(kr)}
                onCheckin={() => onCheckin(kr)}
                onIniciativa={() => onIniciativa(kr)}
                onRemoverIniciativa={(idIniciativa) => onRemoverIniciativa(kr, idIniciativa)}
                onRemover={() => onRemoverKr(objetivo.id, kr)}
              />
            ))}
          </div>
        );
      })}

      <button type="button" className="okr-add-kr" onClick={() => onNovoKr(objetivo.id)}>
        <FontAwesomeIcon icon={faPlus} /> Key Result
      </button>

      {filhos.length > 0 && (
        <div className="okr-filhos">
          <p className="okr-filhos-label">
            <FontAwesomeIcon icon={faSitemap} /> Objetivos filhos (contribuem para este)
          </p>
          <div className="okr-filhos-grid">
            {filhos.map((filho) => (
              <ObjetivoCard
                key={filho.id}
                objetivo={filho}
                filhos={[]}
                ehFilho
                resultadosPorObjetivo={resultadosPorObjetivo}
                iniciativasPorKr={iniciativasPorKr}
                onEditarObjetivo={onEditarObjetivo}
                onRemoverObjetivo={onRemoverObjetivo}
                onNovoKr={onNovoKr}
                onEditarKr={onEditarKr}
                onAvaliarKr={onAvaliarKr}
                onCheckin={onCheckin}
                onIniciativa={onIniciativa}
                onRemoverIniciativa={onRemoverIniciativa}
                onRemoverKr={onRemoverKr}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const formatarData = (iso) => {
  if (!iso) return "";
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-");
  return `${dia}/${mes}/${ano}`;
};

const KrRow = ({ kr, iniciativas, onEditar, onAvaliar, onCheckin, onIniciativa, onRemoverIniciativa, onRemover }) => {
  const ui = confiancaUi(kr.confianca);
  const pct = progressoPercentual(kr);
  const tipoIcon = TIPO_METRICA_ICON[kr.tipo_metrica] || faHashtag;
  const nUi = notaUi(kr.nota_final);
  const prioridade = kr.prioridade || 3;

  return (
    <div className="okr-kr-row" style={{ "--conf-color": ui.color, "--conf-glow": ui.glow }}>
      <div className="okr-kr-icon" style={{ background: `${ui.color}1f`, color: ui.color }}>
        <FontAwesomeIcon icon={tipoIcon} />
      </div>
      <div className="okr-kr-body">
        <div className="okr-kr-top">
          <span className="okr-kr-titulo">
            {kr.titulo}
            {kr.id_resultado_chave_pai && <Badge bg="light" text="dark" className="ms-2">contribui p/ outro KR</Badge>}
            {kr.estrategia_agregacao && <Badge bg="light" text="dark" className="ms-2">soma filhos ({kr.estrategia_agregacao})</Badge>}
            {kr.id_usuario_responsavel && (
              <span className="okr-kr-responsavel" title={`Responsavel: ${kr.responsavel_nome || ""}`}>
                <span className="okr-avatar">{iniciaisDoNome(kr.responsavel_nome)}</span>
                {kr.responsavel_nome}
              </span>
            )}
          </span>
          <span className="okr-kr-valor">
            {Number(kr.valor_inicial)} → <b>{Number(kr.valor_atual)}</b> / {Number(kr.valor_alvo)} {kr.unidade || ""}
          </span>
        </div>
        <div className="okr-kr-bar-wrap">
          <div className="okr-kr-bar-track">
            <div className="okr-kr-bar-fill" style={{ width: `${pct}%`, background: ui.color, boxShadow: `0 0 8px ${ui.glow}` }} />
          </div>
          <span className="okr-kr-bar-pct">{pct}%</span>
        </div>
        <div className="okr-kr-meta">
          <span className="okr-kr-stars" title={`Prioridade ${prioridade}/5`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <FontAwesomeIcon key={n} icon={faStar} className={n <= prioridade ? "ativa" : ""} />
            ))}
          </span>
          {kr.vencimento && (
            <span className="okr-kr-vencimento">
              <FontAwesomeIcon icon={faCalendarDay} /> {formatarData(kr.vencimento)}
            </span>
          )}
          <button
            type="button"
            className="okr-kr-nota-pill"
            style={{ color: nUi.color, background: `${nUi.color}1a`, borderColor: `${nUi.color}44` }}
            onClick={onAvaliar}
            title="Definir nota final"
          >
            {nUi.label}
          </button>
        </div>
        <div className="okr-kr-actions">
          <button type="button" className="okr-icon-btn" onClick={onEditar} title="Editar Key Result">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button type="button" className="okr-mini-btn" onClick={onCheckin}>
            <FontAwesomeIcon icon={faCheck} /> Check-in
          </button>
          <button type="button" className="okr-mini-btn" onClick={onIniciativa}>
            <FontAwesomeIcon icon={faLink} /> Tarefa
          </button>
          <button
            type="button"
            className="okr-icon-btn okr-icon-btn-danger ms-auto"
            title="Excluir Key Result"
            onClick={() => {
              if (window.confirm(`Excluir o Key Result "${kr.titulo}"? Essa acao nao pode ser desfeita.`)) {
                onRemover();
              }
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
        {iniciativas.length > 0 && (
          <div className="okr-kr-iniciativas">
            {iniciativas.map((i) => (
              <span key={i.id_iniciativa} className="okr-iniciativa-chip" title={i.descricao}>
                <span className="okr-avatar">{iniciaisDoNome(i.responsavel_nome)}</span>
                {i.descricao}
                <button type="button" onClick={() => onRemoverIniciativa(i.id_iniciativa)} title="Desvincular">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Visao "Por Area" (COO): um card por area, somando TODOS os KRs dela
// espalhados por qualquer objetivo do ciclo - progresso medio, distribuicao
// de confianca e quem sao os responsaveis, sem precisar abrir objetivo por
// objetivo pra montar esse quadro na cabeca.
const AreaCard = ({ krs, nomeArea, iniciativasPorKr, onEditarKr, onAvaliarKr, onCheckin, onIniciativa, onRemoverIniciativa, onRemoverKr }) => {
  const areaUi = corParaArea(nomeArea);
  const pct = progressoMedio(krs);
  const totalObjetivos = useMemo(() => new Set(krs.map((kr) => kr.objetivo_titulo)).size, [krs]);
  const contagemConfianca = useMemo(() => {
    const contagem = { on_track: 0, at_risk: 0, behind: 0 };
    krs.forEach((kr) => {
      contagem[kr.confianca] = (contagem[kr.confianca] || 0) + 1;
    });
    return contagem;
  }, [krs]);
  const responsaveis = useMemo(() => {
    const mapa = new Map();
    krs.forEach((kr) => {
      if (kr.id_usuario_responsavel && !mapa.has(kr.id_usuario_responsavel)) {
        mapa.set(kr.id_usuario_responsavel, kr.responsavel_nome);
      }
    });
    return Array.from(mapa.values());
  }, [krs]);

  return (
    <div className="okr-objetivo-card" style={{ "--conf-color": areaUi.color, "--conf-glow": areaUi.glow }}>
      <div className="okr-objetivo-head">
        <div className="okr-objetivo-icon" style={{ background: `${areaUi.color}22`, color: areaUi.color, boxShadow: `0 0 18px ${areaUi.glow}` }}>
          <FontAwesomeIcon icon={faLayerGroup} />
        </div>
        <div className="okr-objetivo-heading">
          <h4>{nomeArea}</h4>
          <p className="okr-objetivo-desc">
            {krs.length} Key Result{krs.length === 1 ? "" : "s"} em {totalObjetivos} objetivo{totalObjetivos === 1 ? "" : "s"}
          </p>
        </div>
        {responsaveis.length > 0 && (
          <div className="okr-area-avatares">
            {responsaveis.slice(0, 5).map((nome, i) => (
              <span key={i} className="okr-avatar" title={nome}>{iniciaisDoNome(nome)}</span>
            ))}
          </div>
        )}
      </div>

      <div className="okr-objetivo-progress">
        <div className="okr-objetivo-progress-bar">
          <div className="okr-objetivo-progress-fill" style={{ width: `${pct}%`, background: areaUi.color, boxShadow: `0 0 10px ${areaUi.glow}` }} />
        </div>
        <span className="okr-objetivo-progress-label">
          <strong>{pct}%</strong> progresso medio
        </span>
      </div>

      <div className="okr-area-confianca-resumo">
        <span style={{ color: CONFIANCA_UI.on_track.color }}>
          <FontAwesomeIcon icon={CONFIANCA_UI.on_track.icon} /> {contagemConfianca.on_track} no caminho
        </span>
        <span style={{ color: CONFIANCA_UI.at_risk.color }}>
          <FontAwesomeIcon icon={CONFIANCA_UI.at_risk.icon} /> {contagemConfianca.at_risk} em risco
        </span>
        <span style={{ color: CONFIANCA_UI.behind.color }}>
          <FontAwesomeIcon icon={CONFIANCA_UI.behind.icon} /> {contagemConfianca.behind} atrasado(s)
        </span>
      </div>

      <div className="okr-area-grupo">
        {krs.map((kr) => (
          <div key={kr.id} className="okr-area-kr-wrap">
            <div className="okr-area-kr-objetivo-tag">
              <FontAwesomeIcon icon={faBullseye} /> {kr.objetivo_titulo}
            </div>
            <KrRow
              kr={kr}
              iniciativas={iniciativasPorKr[kr.id] || []}
              onEditar={() => onEditarKr(kr)}
              onAvaliar={() => onAvaliarKr(kr)}
              onCheckin={() => onCheckin(kr)}
              onIniciativa={() => onIniciativa(kr)}
              onRemoverIniciativa={(idIniciativa) => onRemoverIniciativa(kr, idIniciativa)}
              onRemover={() => onRemoverKr(kr.id_objetivo, kr)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Modais ───────────────────────────────────────────────────────────────

const ModalNovoCiclo = ({ onClose, onSalvar }) => {
  const [nome, setNome] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({ nome, dataInicio, dataFim });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao criar o ciclo.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Novo ciclo</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Q1 2027" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Data de inicio</Form.Label>
          <Form.Control type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Data de fim</Form.Label>
          <Form.Control type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !nome || !dataInicio || !dataFim}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalNovaArea = ({ onClose, onSalvar }) => {
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(nome);
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao criar a area.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Nova area</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group>
          <Form.Label>Nome da area</Form.Label>
          <Form.Control value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Comercial, Desenvolvimento, CS..." />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !nome.trim()}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const formatarDataHora = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
};

const ModalAnaliseIA = ({ ciclo, analisando, onClose, onAnalisar, onCriarTarefa }) => {
  const analise = ciclo.analise_ia;

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog okr-modal-dialog-lg" contentClassName="okr-modal-content">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faWandMagicSparkles} className="me-2" />
          Analise de IA — {ciclo.nome}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {ciclo.analise_ia_em && (
          <p className="okr-ia-timestamp">Ultima analise: {formatarDataHora(ciclo.analise_ia_em)}</p>
        )}

        {analisando && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2 mb-0">Analisando o ciclo...</p>
          </div>
        )}

        {!analisando && !analise && (
          <div className="okr-vazio">
            <FontAwesomeIcon icon={faWandMagicSparkles} size="2x" />
            <p>Nenhuma analise ainda. Gere a primeira com o botao abaixo.</p>
          </div>
        )}

        {!analisando && analise && (
          <>
            <h6>Resumo</h6>
            <p className="okr-ia-resumo">{analise.resumo || "Sem resumo."}</p>

            <h6>
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" style={{ color: "#f59e0b" }} />
              Riscos
            </h6>
            {analise.riscos?.length > 0 ? (
              <ul className="okr-ia-lista">
                {analise.riscos.map((r, i) => (
                  <li key={i}>
                    <strong>{r.objetivo}{r.kr ? ` — ${r.kr}` : ""}:</strong> {r.descricao}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="okr-ia-vazio-lista">Nenhum risco relevante identificado.</p>
            )}

            <h6>
              <FontAwesomeIcon icon={faListCheck} className="me-2" style={{ color: "#10b981" }} />
              Acoes sugeridas
            </h6>
            {analise.acoesSugeridas?.length > 0 ? (
              <ul className="okr-ia-lista okr-ia-lista-acoes">
                {analise.acoesSugeridas.map((a, i) => (
                  <li key={i}>
                    <div>
                      <strong>{a.titulo}</strong>{a.kr ? ` (${a.kr})` : ""} — {a.motivo}
                    </div>
                    {a.idKr && (
                      <button
                        type="button"
                        className="okr-mini-btn okr-ia-criar-tarefa"
                        onClick={() => onCriarTarefa(a.idKr, a.titulo)}
                      >
                        <FontAwesomeIcon icon={faLink} /> Criar tarefa
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="okr-ia-vazio-lista">Nenhuma acao sugerida no momento.</p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Fechar</Button>
        <Button variant="primary" onClick={onAnalisar} disabled={analisando}>
          <FontAwesomeIcon icon={faWandMagicSparkles} className="me-2" />
          {analise ? "Atualizar analise" : "Gerar analise"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalNovoObjetivo = ({ objetivosExistentes, onClose, onSalvar }) => {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [idObjetivoPai, setIdObjetivoPai] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  // So objetivos que ainda nao tem pai podem virar pai (limite de 1 nivel).
  const candidatosPai = objetivosExistentes.filter((o) => !o.id_objetivo_pai);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({ titulo, descricao, idObjetivoPai: idObjetivoPai || null });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao criar o objetivo.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Novo objetivo</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Titulo</Form.Label>
          <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Crescer 30% em 2027" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Descricao (opcional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </Form.Group>
        {candidatosPai.length > 0 && (
          <Form.Group>
            <Form.Label>Contribui para (opcional)</Form.Label>
            <Form.Select value={idObjetivoPai} onChange={(e) => setIdObjetivoPai(e.target.value)}>
              <option value="">Nenhum - objetivo independente</option>
              {candidatosPai.map((o) => (
                <option key={o.id} value={o.id}>{o.titulo}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !titulo.trim()}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalEditarObjetivo = ({ objetivo, objetivosExistentes, onClose, onSalvar }) => {
  const [titulo, setTitulo] = useState(objetivo.titulo);
  const [descricao, setDescricao] = useState(objetivo.descricao || "");
  const [idObjetivoPai, setIdObjetivoPai] = useState(objetivo.id_objetivo_pai || "");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  // Mesma regra de criarObjetivo (1 nivel): so pode virar pai quem ainda nao
  // tem pai, excluindo o proprio objetivo e os filhos diretos dele (senao
  // criaria um ciclo).
  const candidatosPai = objetivosExistentes.filter(
    (o) => o.id !== objetivo.id && !o.id_objetivo_pai && o.id_objetivo_pai !== objetivo.id
  );

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({ titulo, descricao, idObjetivoPai: idObjetivoPai || null });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao atualizar o objetivo.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Editar objetivo</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Titulo</Form.Label>
          <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Descricao (opcional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        </Form.Group>
        {candidatosPai.length > 0 && (
          <Form.Group>
            <Form.Label>Contribui para (opcional)</Form.Label>
            <Form.Select value={idObjetivoPai} onChange={(e) => setIdObjetivoPai(e.target.value)}>
              <option value="">Nenhum - objetivo independente</option>
              {candidatosPai.map((o) => (
                <option key={o.id} value={o.id}>{o.titulo}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !titulo.trim()}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalNovoKr = ({ idObjetivo, areas, membros, resultadosChaveExistentes, onClose, onSalvar }) => {
  const [titulo, setTitulo] = useState("");
  const [idArea, setIdArea] = useState("");
  const [tipoMetrica, setTipoMetrica] = useState("numero");
  const [valorInicial, setValorInicial] = useState("0");
  const [valorAlvo, setValorAlvo] = useState("");
  const [unidade, setUnidade] = useState("");
  const [idUsuarioResponsavel, setIdUsuarioResponsavel] = useState("");
  const [prioridade, setPrioridade] = useState(3);
  const [vencimento, setVencimento] = useState("");
  const [idResultadoChavePai, setIdResultadoChavePai] = useState("");
  const [estrategiaAgregacao, setEstrategiaAgregacao] = useState("soma");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  // So KRs que ainda nao sao filhos de outro podem virar pai (limite de 1 nivel).
  const candidatosPai = resultadosChaveExistentes.filter((r) => !r.id_resultado_chave_pai);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({
        idArea: idArea || null,
        idResultadoChavePai: idResultadoChavePai || null,
        estrategiaAgregacao: idResultadoChavePai ? estrategiaAgregacao : null,
        titulo,
        tipoMetrica,
        valorInicial,
        valorAlvo,
        unidade,
        idUsuarioResponsavel: idUsuarioResponsavel || null,
        prioridade,
        vencimento: vencimento || null,
      });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao criar o Key Result.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Novo Key Result</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Titulo</Form.Label>
          <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Reduzir tempo ate o primeiro valor" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Area</Form.Label>
          <Form.Select value={idArea} onChange={(e) => setIdArea(e.target.value)}>
            <option value="">Geral (padrao)</option>
            {areas.filter((a) => !a.padrao).map((a) => (
              <option key={a.id} value={a.id}>{a.nome}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Responsavel (opcional)</Form.Label>
          <Form.Select value={idUsuarioResponsavel} onChange={(e) => setIdUsuarioResponsavel(e.target.value)}>
            <option value="">Sem responsavel definido</option>
            {membros.map((m) => (
              <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>{m.descricao || m.nome || m.email}</option>
            ))}
          </Form.Select>
          <Form.Text muted>Pode ser trocado depois, se a pessoa nao estiver performando.</Form.Text>
        </Form.Group>
        <div className="d-flex gap-2 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label>Tipo</Form.Label>
            <Form.Select value={tipoMetrica} onChange={(e) => setTipoMetrica(e.target.value)}>
              {TIPOS_METRICA.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Unidade</Form.Label>
            <Form.Control value={unidade} onChange={(e) => setUnidade(e.target.value)} placeholder="dias, R$, %..." />
          </Form.Group>
        </div>
        <div className="d-flex gap-2 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label>Valor inicial</Form.Label>
            <Form.Control type="number" value={valorInicial} onChange={(e) => setValorInicial(e.target.value)} />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Valor alvo</Form.Label>
            <Form.Control type="number" value={valorAlvo} onChange={(e) => setValorAlvo(e.target.value)} />
          </Form.Group>
        </div>
        <div className="d-flex gap-2 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label>Prioridade</Form.Label>
            <Form.Select value={prioridade} onChange={(e) => setPrioridade(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{"★".repeat(n)}{"☆".repeat(5 - n)}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Vencimento (opcional)</Form.Label>
            <Form.Control type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
          </Form.Group>
        </div>
        {candidatosPai.length > 0 && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Contribui para outro Key Result (opcional)</Form.Label>
              <Form.Select value={idResultadoChavePai} onChange={(e) => setIdResultadoChavePai(e.target.value)}>
                <option value="">Nenhum - Key Result independente</option>
                {candidatosPai.map((r) => (
                  <option key={r.id} value={r.id}>{r.titulo}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {idResultadoChavePai && (
              <Form.Group>
                <Form.Label>Como o KR-pai deve somar os filhos</Form.Label>
                <Form.Select value={estrategiaAgregacao} onChange={(e) => setEstrategiaAgregacao(e.target.value)}>
                  {ESTRATEGIAS_AGREGACAO.map((e2) => <option key={e2.valor} value={e2.valor}>{e2.label}</option>)}
                </Form.Select>
                <Form.Text muted>Define a estrategia do KR-pai escolhido, nao deste aqui.</Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !titulo.trim() || valorAlvo === ""}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalEditarKr = ({ kr, areas, membros, onClose, onSalvar }) => {
  const [titulo, setTitulo] = useState(kr.titulo);
  const [idArea, setIdArea] = useState(kr.id_area || "");
  const [tipoMetrica, setTipoMetrica] = useState(kr.tipo_metrica || "numero");
  const [valorInicial, setValorInicial] = useState(String(kr.valor_inicial ?? "0"));
  const [valorAlvo, setValorAlvo] = useState(String(kr.valor_alvo ?? ""));
  const [unidade, setUnidade] = useState(kr.unidade || "");
  const [idUsuarioResponsavel, setIdUsuarioResponsavel] = useState(kr.id_usuario_responsavel || "");
  const [prioridade, setPrioridade] = useState(kr.prioridade || 3);
  const [vencimento, setVencimento] = useState(kr.vencimento ? String(kr.vencimento).slice(0, 10) : "");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({
        idArea: idArea || null,
        titulo,
        tipoMetrica,
        valorInicial,
        valorAlvo,
        unidade,
        idUsuarioResponsavel: idUsuarioResponsavel || null,
        prioridade,
        vencimento: vencimento || null,
      });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao atualizar o Key Result.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Editar Key Result</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Titulo</Form.Label>
          <Form.Control value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Area</Form.Label>
          <Form.Select value={idArea} onChange={(e) => setIdArea(e.target.value)}>
            <option value="">Geral (padrao)</option>
            {areas.filter((a) => !a.padrao).map((a) => (
              <option key={a.id} value={a.id}>{a.nome}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Responsavel (opcional)</Form.Label>
          <Form.Select value={idUsuarioResponsavel} onChange={(e) => setIdUsuarioResponsavel(e.target.value)}>
            <option value="">Sem responsavel definido</option>
            {membros.map((m) => (
              <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>{m.descricao || m.nome || m.email}</option>
            ))}
          </Form.Select>
          <Form.Text muted>Pode ser trocado a qualquer momento, se a pessoa nao estiver performando.</Form.Text>
        </Form.Group>
        <div className="d-flex gap-2 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label>Tipo</Form.Label>
            <Form.Select value={tipoMetrica} onChange={(e) => setTipoMetrica(e.target.value)}>
              {TIPOS_METRICA.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Unidade</Form.Label>
            <Form.Control value={unidade} onChange={(e) => setUnidade(e.target.value)} placeholder="dias, R$, %..." />
          </Form.Group>
        </div>
        <div className="d-flex gap-2 mb-3">
          <Form.Group className="flex-fill">
            <Form.Label>Valor inicial</Form.Label>
            <Form.Control type="number" value={valorInicial} onChange={(e) => setValorInicial(e.target.value)} />
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Valor alvo</Form.Label>
            <Form.Control type="number" value={valorAlvo} onChange={(e) => setValorAlvo(e.target.value)} />
          </Form.Group>
        </div>
        <div className="d-flex gap-2">
          <Form.Group className="flex-fill">
            <Form.Label>Prioridade</Form.Label>
            <Form.Select value={prioridade} onChange={(e) => setPrioridade(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{"★".repeat(n)}{"☆".repeat(5 - n)}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="flex-fill">
            <Form.Label>Vencimento (opcional)</Form.Label>
            <Form.Control type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || !titulo.trim() || valorAlvo === ""}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalAvaliarKr = ({ kr, onClose, onSalvar }) => {
  const [nota, setNota] = useState(kr.nota_final ?? "");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  const preview = notaUi(nota === "" ? null : Number(nota));

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(nota === "" ? null : Number(nota));
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao definir a nota final.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Avaliar — {kr.titulo}</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Nota final (0 a 100)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            max="100"
            value={nota}
            onChange={(e) => setNota(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="Deixe em branco para remover a nota"
            autoFocus
          />
        </Form.Group>
        <span
          className="okr-kr-nota-pill"
          style={{ color: preview.color, background: `${preview.color}1a`, borderColor: `${preview.color}44` }}
        >
          {preview.label}
        </span>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando}>Salvar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalCheckin = ({ kr, onClose, onSalvar }) => {
  const [valor, setValor] = useState(String(kr.valor_atual));
  const [confianca, setConfianca] = useState("on_track");
  const [comentario, setComentario] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar({ valor, confianca, comentario });
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao registrar o check-in.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Check-in — {kr.titulo}</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Novo valor {kr.unidade ? `(${kr.unidade})` : ""}</Form.Label>
          <Form.Control type="number" value={valor} onChange={(e) => setValor(e.target.value)} autoFocus />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Confianca</Form.Label>
          <Form.Select value={confianca} onChange={(e) => setConfianca(e.target.value)}>
            <option value="on_track">No caminho</option>
            <option value="at_risk">Em risco</option>
            <option value="behind">Atrasado</option>
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Comentario (opcional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={comentario} onChange={(e) => setComentario(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={salvar} disabled={salvando || valor === ""}>Salvar check-in</Button>
      </Modal.Footer>
    </Modal>
  );
};

const ModalIniciativa = ({ kr, membros, descricaoInicial, onClose, onCriar, onVincular }) => {
  const [modo, setModo] = useState("criar"); // "criar" | "vincular"
  const [descricao, setDescricao] = useState(descricaoInicial || "");
  const [idUsuarioResponsavel, setIdUsuarioResponsavel] = useState("");
  const [vencimento, setVencimento] = useState("");
  const [idTarefa, setIdTarefa] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      if (modo === "criar") {
        await onCriar({ descricao, idUsuarioResponsavel: idUsuarioResponsavel || null, vencimento: vencimento || null });
      } else {
        await onVincular(Number(idTarefa));
      }
    } catch (e) {
      setErro(e?.response?.data?.message || "Erro ao vincular a tarefa.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show onHide={onClose} centered dialogClassName="okr-modal-dialog" contentClassName="okr-modal-content">
      <Modal.Header closeButton><Modal.Title>Tarefa para — {kr.titulo}</Modal.Title></Modal.Header>
      <Modal.Body>
        {erro && <Alert variant="danger">{erro}</Alert>}
        <div className="d-flex gap-2 mb-3">
          <Button size="sm" variant={modo === "criar" ? "primary" : "outline-primary"} onClick={() => setModo("criar")}>Criar nova</Button>
          <Button size="sm" variant={modo === "vincular" ? "primary" : "outline-primary"} onClick={() => setModo("vincular")}>Vincular existente</Button>
        </div>

        {modo === "criar" ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Descricao da tarefa</Form.Label>
              <Form.Control value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="O que precisa ser feito" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Responsavel</Form.Label>
              <Form.Select value={idUsuarioResponsavel} onChange={(e) => setIdUsuarioResponsavel(e.target.value)}>
                <option value="">Sem responsavel definido</option>
                {membros.map((m) => (
                  <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>{m.descricao || m.nome || m.email}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Vencimento (opcional)</Form.Label>
              <Form.Control type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
            </Form.Group>
          </>
        ) : (
          <Form.Group>
            <Form.Label>Id da tarefa existente</Form.Label>
            <Form.Control type="number" value={idTarefa} onChange={(e) => setIdTarefa(e.target.value)} placeholder="Ex.: 482" />
            <Form.Text muted>Pegue o id na visao em lista ou no kanban de Tarefas.</Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={salvar}
          disabled={salvando || (modo === "criar" ? !descricao.trim() : !idTarefa)}
        >
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OkrPage;
